import { useState, useCallback, memo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { ROLES } from "../../uitls/Utils";
import { signIn } from "../../api/userApi";
import { setUser } from "../../features/user/userSlice";
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { Checkbox, FormControlLabel, Link as MuiLink } from "@mui/material";

const getDashboardRoute = (roleId) => {
  switch (roleId) {
    case ROLES.admin:
      return "/admin/dashboard";
    case ROLES.mgr:
      return "/mgr/dashboard";
    case ROLES.hr:
      return "/hr/dashboard";
    case ROLES.emp:
      return "/emp/dashboard";
    default:
      return "/emp/dashboard";
  }
};

const SignIn = memo(() => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { userError } = useSelector((state) => state.userState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberPassword, setRememberPassword] = useState(false);
  

  const [formValues, setFormValues] = useState({
    corporateEmail: "",
    password: "",
  });

  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    const savedCredentials = localStorage.getItem("rememberedUser");
    if (savedCredentials) {
      try {
        const { email, password } = JSON.parse(savedCredentials);
        setFormValues({
          corporateEmail: email || "",
          password: password || "",
        });
        setRememberPassword(true);
      } catch (error) {
        console.error("Error loading saved credentials:", error);
        localStorage.removeItem("rememberedUser");
      }
    }
  }, []);

  const handleEmailChange = useCallback(
    (e) => {
      const value = e.target.value;
      setFormValues((prev) => ({ ...prev, corporateEmail: value }));

      if (formErrors.corporateEmail) {
        setFormErrors((prev) => ({ ...prev, corporateEmail: "" }));
      }
    },
    [formErrors.corporateEmail]
  );

  const handlePasswordChange = useCallback(
    (e) => {
      const value = e.target.value;
      setFormValues((prev) => ({ ...prev, password: value }));

      if (formErrors.password) {
        setFormErrors((prev) => ({ ...prev, password: "" }));
      }
    },
    [formErrors.password]
  );

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const rememberPasswordChange = useCallback((e) => {
    const isChecked = e.target.checked;
    setRememberPassword(isChecked);
    // If unchecking, remove saved credentials and clear form
    if (!isChecked) {
      localStorage.removeItem("rememberedUser");
      // Clear the form fields when unchecking
      setFormValues({
        corporateEmail: "",
        password: "",
      });
    }
    // Note: Credentials will be saved on successful login in handleLoginSuccess
  }, []);

const handleLoginSuccess = useCallback((loginResponse, shouldRemember = false) => {
  const { token, user, roleId, mfaEnabled } = loginResponse;
  
  // Use the passed shouldRemember parameter or fall back to state
  const willRemember = shouldRemember !== undefined ? shouldRemember : rememberPassword;

  const mfaSetupData = loginResponse?.mfaSetupResponse || {};
  const secret = mfaSetupData?.secret || loginResponse?.secretKey || loginResponse?.secret;
  const qrCodeUrl = mfaSetupData?.qrCodeUrl || loginResponse?.qrCodeUrl;
  const manualEntryKey = mfaSetupData?.manualEntryKey || loginResponse?.manualEntryKey;

  const mfaRequired = mfaEnabled === true || !!(qrCodeUrl || secret);
  const mfaVerified = false;

  console.log("Login MFA Check:", { 
    mfaEnabled, 
    hasQR: !!qrCodeUrl, 
    hasSecret: !!secret, 
    mfaRequired 
  });

  // Save credentials if remember password is checked
  if (willRemember && formValues.corporateEmail && formValues.password) {
    try {
      const credentialsToSave = {
        email: formValues.corporateEmail.trim(),
        password: formValues.password,
      };
      localStorage.setItem("rememberedUser", JSON.stringify(credentialsToSave));
      console.log("Credentials saved to localStorage");
    } catch (error) {
      console.error("Error saving credentials:", error);
    }
  } else {
    // Remove saved credentials if remember password is not checked
    localStorage.removeItem("rememberedUser");
    console.log("Credentials removed from localStorage");
  }

  dispatch(setUser({
    token: token || null,
    user: user || null,
    roleId: roleId || null,
    mfaRequired,
    mfaVerified
  }));

  if (mfaRequired) {
    console.log("MFA required, redirecting to verification");
    navigate("/mfa-setup", {
      state: {
        corporateEmail: formValues.corporateEmail,
        password: formValues.password,
        mfaEnabled: mfaEnabled,
        roleId: roleId,
        secret: secret,
        qrCodeUrl: qrCodeUrl,
        manualEntryKey: manualEntryKey,
      },
      replace: true,
    });
  } else {
    console.log("No MFA required, redirecting to dashboard");
    const dashboardRoute = getDashboardRoute(roleId);
    navigate(dashboardRoute, { replace: true });
  }
}, [dispatch, navigate, formValues.corporateEmail, formValues.password, rememberPassword]);

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);

    const errors = {};
    if (!formValues.corporateEmail) {
      errors.corporateEmail = "Email address is required";
    } else if (!/\S+@\S+\.\S+/.test(formValues.corporateEmail)) {
      errors.corporateEmail = "Please enter a valid email address";
    }

    if (!formValues.password) {
      errors.password = "Password is required";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setIsSubmitting(false);
      return;
    }

    try {
      console.log("Remember password state before login:", rememberPassword);
      const loginResponse = await dispatch(signIn(formValues)).unwrap();
      console.log("Login response:", loginResponse);
      // Pass rememberPassword state directly to ensure it's current
      handleLoginSuccess(loginResponse, rememberPassword);
    } catch (error) {
      console.error("Sign in error:", error);
    } finally {
      setIsSubmitting(false);
    }
  }, [formValues, dispatch, handleLoginSuccess, rememberPassword]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  return (
    <div className="min-h-screen brand-header flex items-center justify-center p-4 md:p-8">
      {/* Main Container with Shadow */}
      <div className="w-full max-w-6xl bg-white rounded-2xl md:rounded-3xl shadow-2xl overflow-hidden relative">
        <div className="flex flex-col lg:flex-row min-h-[400px] lg:min-h-[600px]">
          {/* Form Section - Left */}
          <div className="w-full lg:w-1/2 flex items-center justify-center p-6 md:p-8 lg:p-16">
            <div className="w-full max-w-md">
              {/* Form Title */}
              <div className="text-center mb-4 md:mb-6 lg:mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 md:mb-3">
                  Sign In
                </h2>
              </div>

              {/* Error Display */}
              {userError && (
                <div className="mb-3 md:mb-4 lg:mb-6 p-3 md:p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{userError}</p>
                </div>
              )}

              {/* Form Fields */}
              <div className="space-y-3 md:space-y-4 lg:space-y-6">
                {/* Email Field */}
                <div>
                  <label
                    htmlFor="corporateEmail"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 md:pl-4 flex items-center pointer-events-none">
                      <EmailIcon className="h-4 w-4 md:h-5 md:w-5 text-gray-400" />
                    </div>
                    <input
                      id="corporateEmail"
                      type="email"
                      placeholder="Enter your email address"
                      value={formValues?.corporateEmail || ""}
                      onChange={handleEmailChange}
                      autoFocus={true}
                      className="block w-full pl-10 md:pl-12 pr-3 md:pr-4 py-3 md:py-4 border border-gray-300 rounded-lg md:rounded-xl focus:ring-2 focus:ring-[#135e96] focus:border-transparent transition-all duration-200 text-base md:text-lg"
                    />
                  </div>
                  {formErrors?.corporateEmail && (
                    <p className="mt-1 md:mt-2 text-sm text-red-600">
                      {formErrors.corporateEmail}
                    </p>
                  )}
                </div>

                {/* Password Field */}
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 md:pl-4 flex items-center pointer-events-none">
                      <LockIcon className="h-4 w-4 md:h-5 md:w-5 text-gray-400" />
                    </div>
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={formValues?.password || ""}
                      onChange={handlePasswordChange}
                      onKeyDown={handleKeyDown}
                      className="block w-full pl-10 md:pl-12 pr-10 md:pr-12 py-3 md:py-4 border border-gray-300 rounded-lg md:rounded-xl focus:ring-2 focus:ring-[#135e96] focus:border-transparent transition-all duration-200 text-base md:text-lg"
                    />
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="absolute inset-y-0 right-0 pr-3 md:pr-4 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? (
                        <VisibilityOffIcon className="h-4 w-4 md:h-5 md:w-5" />
                      ) : (
                        <VisibilityIcon className="h-4 w-4 md:h-5 md:w-5" />
                      )}
                    </button>
                  </div>
                  {formErrors?.password && (
                    <p className="mt-1 md:mt-2 text-sm text-red-600">
                      {formErrors.password}
                    </p>
                  )}
                </div>
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between mt-4 md:mt-6">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rememberPassword}
                      onChange={rememberPasswordChange}
                      sx={{
                        color: "#3B82F6",
                        "&.Mui-checked": {
                          color: "#3B82F6",
                        },
                      }}
                    />
                  }
                  label={
                    <span className="text-sm text-gray-600">Remember me</span>
                  }
                />
                <MuiLink
                  component="button"
                  onClick={() => navigate("/forgot-password")}
                  className="text-sm text-[#F5A623] hover:text-[#d88e1a]"
                >
                  Forgot Password?
                </MuiLink>
              </div>

              {/* Submit Button */}
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="w-full mt-4 md:mt-6 lg:mt-8 py-3 md:py-4 px-4 brand-btn-primary rounded-lg md:rounded-xl transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed text-base md:text-lg ripple"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 md:h-5 md:w-5 border-2 border-white border-t-transparent mr-2"></div>
                    <span>Signing In...</span>
                  </div>
                ) : (
                  "Sign In"
                )}
              </button>
            </div>
          </div>

          {/* Blue Design Section - Right - Hidden on Mobile */}
          <div className="hidden lg:block w-1/2 relative overflow-hidden">
            {/* Large Blue Abstract Shape */}
            <div className="absolute top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2 w-[180%] h-[122%] brand-sidebar rounded-full"></div>

            {/* White Text Overlay */}
            <div className="relative z-10 h-full flex flex-col justify-center items-center px-16">
              <div className="text-white text-center max-w-md">
                <h1 className="text-5xl font-bold mb-4 tracking-wide ml-7">
                  Welcome Back
                </h1>
                <p className="text-xl leading-relaxed text-left mb-8 ml-15">
                  Empowering Performance, Driving Growth
                </p>
                <div className="space-y-3 text-left ml-15">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-300 rounded-full mr-3"></div>
                    <span className="text-lg">Employee Management</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-300 rounded-full mr-3"></div>
                    <span className="text-lg">Performance Tracking</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-300 rounded-full mr-3"></div>
                    <span className="text-lg">Appraisal System</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-300 rounded-full mr-3"></div>
                    <span className="text-lg">Real-time Analytics</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Small Blue Spheres - Outside Abstract Shape - Hidden on Mobile */}
        <div className="hidden lg:block absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-12 z-20 ml-4">
          <div className="w-16 h-16 brand-sidebar rounded-full"></div>
          <div className="w-12 h-12 brand-sidebar rounded-full"></div>
        </div>
      </div>
    </div>
  );
});

SignIn.displayName = "SignIn";

export default SignIn;